import axios from 'axios'
import { Auth } from 'aws-amplify'
import config from '../config'
import { showErrorToast } from './messageToast'

const DATABASE_UNAVAILABLE_CODE = 'DATABASE_UNAVAILABLE'
let lastDbDownToastAt = 0

const notifyDatabaseUnavailable = error => {
  const status = error?.response?.status
  const data = error?.response?.data
  const isDbDown =
    status === 503 &&
    (data?.code === DATABASE_UNAVAILABLE_CODE ||
      /database.*(unavailable|down)/i.test(data?.message || ''))

  if (!isDbDown) return

  const now = Date.now()
  // Avoid toast spam when many requests fail at once
  if (now - lastDbDownToastAt < 15_000) return
  lastDbDownToastAt = now

  showErrorToast(
    data?.message ||
      'Database service is currently unavailable. Please try again shortly.',
  )
}

const expressAPI = axios.create({ baseURL: config.apiGateway.EXPRESS_URL })

expressAPI.interceptors.request.use(async requestConfig => {
  try {
    const session = await Auth.currentSession()
    const accessToken = session.getAccessToken().getJwtToken()
    if (accessToken) {
      requestConfig.headers.Authorization = accessToken
    }
  } catch (error) {
    // User not authenticated, will be handled by 401 response
    console.log('No auth session available')
  }
  return requestConfig
})

// Attempt token refresh on 401 responses for express API
expressAPI.interceptors.response.use(
  response => response,
  async error => {
    notifyDatabaseUnavailable(error)

    const originalRequest = error.config
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true
      try {
        // Try to refresh the session
        const session = await Auth.currentSession()
        const accessToken = session.getAccessToken().getJwtToken()
        if (accessToken) {
          originalRequest.headers.Authorization = accessToken
          return expressAPI(originalRequest)
        }
      } catch (e) {
        // Refresh failed, redirect to login or handle as needed
        console.log('Token refresh failed', e)
      }
    }
    return Promise.reject(error)
  },
)

const lambdaAPI = axios.create({ baseURL: config.apiGateway.URL })
lambdaAPI.interceptors.request.use(async requestConfig => {
  try {
    const session = await Auth.currentSession()
    const idToken = session.getIdToken().getJwtToken()
    if (idToken) {
      requestConfig.headers.Authorization = idToken
    }
  } catch (error) {
    console.log('No auth session available')
  }
  return requestConfig
})

// Optional: mirror 401 handling for lambda API
lambdaAPI.interceptors.response.use(
  response => response,
  async error => {
    notifyDatabaseUnavailable(error)

    const originalRequest = error.config
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true
      try {
        const session = await Auth.currentSession()
        const idToken = session.getIdToken().getJwtToken()
        if (idToken) {
          originalRequest.headers.Authorization = idToken
          return lambdaAPI(originalRequest)
        }
      } catch (e) {
        console.log('Token refresh failed', e)
      }
    }
    return Promise.reject(error)
  },
)

// Light wrapper to mimic Amplify API signature used across the dashboard
// Usage remains: API.get('api', 'path'), API.post('api2', 'path', { body })
const instanceByName = name => {
  if (name === 'api2') return expressAPI
  return lambdaAPI // default maps to lambda
}

const API = {
  get: (name, path, options = {}) =>
    instanceByName(name)
      .get(path, {
        headers: options.headers,
        params: options.queryStringParameters,
      })
      .then(r => r.data),
  post: (name, path, options = {}) =>
    instanceByName(name)
      .post(path, options.body, {
        headers: options.headers,
        params: options.queryStringParameters,
        timeout: options.timeout,
      })
      .then(r => r.data),
  put: (name, path, options = {}) =>
    instanceByName(name)
      .put(path, options.body, { headers: options.headers })
      .then(r => r.data),
  patch: (name, path, options = {}) =>
    instanceByName(name)
      .patch(path, options.body, { headers: options.headers })
      .then(r => r.data),
  del: (name, path, options = {}) =>
    instanceByName(name)
      .delete(path, { headers: options.headers, data: options.body })
      .then(r => r.data),
  delete: (name, path, options = {}) =>
    instanceByName(name)
      .delete(path, { headers: options.headers, data: options.body })
      .then(r => r.data),
}

export { expressAPI, lambdaAPI, API }
